/**
 * devTools.js - Developer Console Testing Tools
 * Baba AI - Run commands in browser console for testing
 * 
 * Usage: Open browser console (F12) and type commands
 * 
 * Updated: November 26, 2025
 * NEW: Time Awareness, MCS Auto-Fetch, User Adaptation commands
 */

import googleDriveService from './services/googleDriveService';
import vectorService from './services/vectorService';
import tokenService from './services/tokenService';
import timeAwareness from './services/timeAwareness';
import mcsAutoFetch from './services/mcsAutoFetch';
import userAdaptation from './services/userAdaptation';
import stateDetection from './services/stateDetection';

// Expose services globally for testing
window.googleDriveService = googleDriveService;
window.vectorService = vectorService;
window.tokenService = tokenService;
window.timeAwareness = timeAwareness;
window.mcsAutoFetch = mcsAutoFetch;
window.userAdaptation = userAdaptation;
window.stateDetection = stateDetection;

// Main test object
window.babaTest = {
  // ==========================================
  // GOOGLE DRIVE
  // ==========================================
  
  gdriveStatus: () => {
    const isAuth = googleDriveService.isAuthenticated();
    const hasToken = !!localStorage.getItem('gdrive_access_token');
    console.log('GDrive Status:', { authenticated: isAuth, hasToken });
    return isAuth;
  },
  
  gdriveConnect: () => {
    googleDriveService.authenticate();
    console.log('Opening Google OAuth...');
  },
  
  gdriveDisconnect: () => {
    googleDriveService.disconnect();
    console.log('✅ GDrive disconnected');
  },

  // ==========================================
  // VECTORIZE / MEMORY
  // ==========================================
  
  vectorHealth: async () => {
    try {
      const health = await vectorService.healthCheck();
      console.table(health);
      return health;
    } catch (e) {
      console.error('Vectorize error:', e);
    }
  },
  
  vectorStats: async () => {
    try {
      const stats = await vectorService.getStats();
      console.table(stats);
      return stats;
    } catch (e) {
      console.error('Stats error:', e);
    }
  },
  
  vectorSearch: async (query) => {
    try {
      const results = await vectorService.search(query);
      console.log(`Found ${results.length} results for "${query}"`);
      results.forEach((r, i) => {
        console.log(`${i+1}. [${r.score.toFixed(3)}] ${r.metadata?.title || 'Untitled'}`);
        console.log(`   ${r.content?.substring(0, 100)}...`);
      });
      return results;
    } catch (e) {
      console.error('Search error:', e);
    }
  },

  // ==========================================
  // TOKENS
  // ==========================================
  
  tokenCount: (text) => {
    const count = tokenService.countTokens(text);
    console.log(`Token count: ${count}`);
    return count;
  },
  
  tokenEstimate: () => {
    const chats = JSON.parse(localStorage.getItem('baba-ai-chats') || '[]');
    if (chats.length === 0) {
      console.log('No chats found');
      return 0;
    }
    const current = chats[0];
    const total = tokenService.countChatTokens(current.messages || []);
    console.log(`Current chat tokens: ${total} (${Math.round(total/95000*100)}% of 95K limit)`);
    return total;
  },

  // ==========================================
  // TIME AWARENESS (NEW)
  // ==========================================
  
  timeContext: () => {
    const ctx = timeAwareness.getCurrentContext();
    console.table(ctx);
    return ctx;
  },
  
  timePatterns: () => {
    const patterns = timeAwareness.getPatternInsights();
    console.table(patterns);
    console.log('Hourly distribution:', patterns.hourlyDistribution);
    return patterns;
  },
  
  sessionDuration: () => {
    const duration = timeAwareness.getFormattedSessionDuration();
    console.log(`Current session: ${duration}`);
    return duration;
  },
  
  timeState: () => {
    const state = timeAwareness.detectTimeBasedState();
    console.table(state);
    return state;
  },

  // ==========================================
  // MCS AUTO-FETCH (NEW)
  // ==========================================
  
  mcsStatus: () => {
    const status = mcsAutoFetch.getStatus();
    console.table(status);
    if (status.cacheInfo) {
      console.log('Cache info:', status.cacheInfo);
    }
    return status;
  },
  
  mcsRefresh: async () => {
    console.log('Forcing MCS refresh...');
    const content = await mcsAutoFetch.forceRefresh();
    if (content) {
      console.log('✅ MCS refreshed successfully');
      console.log(`Length: ${content.length} chars`);
      console.log('Preview:', content.substring(0, 200) + '...');
    } else {
      console.log('❌ MCS refresh failed');
    }
    return !!content;
  },
  
  mcsPreview: () => {
    const content = mcsAutoFetch.getCurrentMCS();
    if (content) {
      console.log('MCS Content Preview:');
      console.log(content.substring(0, 500) + '...');
      console.log(`Total length: ${content.length} chars`);
    } else {
      console.log('No MCS loaded');
    }
    return content;
  },

  // ==========================================
  // USER ADAPTATION (NEW)
  // ==========================================
  
  userProfile: () => {
    const summary = userAdaptation.getProfileSummary();
    console.table(summary);
    console.log('Style:', summary.style);
    console.log('Spectrum:', summary.spectrum);
    console.log('Safety:', summary.safety);
    return summary;
  },
  
  adaptationInstructions: () => {
    const instructions = userAdaptation.getAdaptationInstructions();
    console.log('Current adaptation instructions:');
    console.log(instructions || '[None - profile still building]');
    return instructions;
  },
  
  clearProfile: () => {
    userAdaptation.clearProfile();
    console.log('✅ User profile cleared');
  },
  
  simulateMessage: (text) => {
    console.log(`Simulating message analysis: "${text}"`);
    userAdaptation.analyzeMessage(text);
    console.log('Updated profile:');
    console.table(userAdaptation.getProfileSummary());
  },

  // ==========================================
  // STATE DETECTION (FULL SPECTRUM)
  // ==========================================

  spectrumScores: () => {
    const scores = userAdaptation.getSpectrumScores();
    console.log('=== FULL SPECTRUM SCORES ===');
    console.table(scores);
    return scores;
  },

  fullState: () => {
    const state = userAdaptation.getFullStateData();
    console.log('=== COMPLETE STATE DATA ===');
    console.log('Profile:', state.profile);
    console.log('ADHD:', state.adhd);
    console.log('Autism:', state.autism);
    console.log('Bipolar:', state.bipolar);
    console.log('Neurotypical:', state.neurotypical);
    console.log('Anxiety:', state.anxiety);
    console.log('Psychosis Safety:', state.psychosis);
    return state;
  },

  safetyStatus: () => {
    const safety = userAdaptation.getSafetyStatus();
    console.log('=== SAFETY STATUS ===');
    console.table(safety);
    if (safety.needsAttention) {
      console.warn('⚠️ Safety flags active:', safety.flags);
    } else {
      console.log('✅ No safety concerns');
    }
    return safety;
  },

  adhdScore: () => {
    const state = userAdaptation.getFullStateData();
    console.log('=== ADHD ANALYSIS ===');
    console.log('Score:', state.adhd.score);
    console.log('Subtype:', state.adhd.subtype);
    console.table(state.adhd.indicators);
    return state.adhd;
  },

  autismScore: () => {
    const state = userAdaptation.getFullStateData();
    console.log('=== AUTISM SPECTRUM ANALYSIS ===');
    console.log('Score:', state.autism.score);
    console.table(state.autism.indicators);
    return state.autism;
  },

  bipolarScore: () => {
    const state = userAdaptation.getFullStateData();
    console.log('=== BIPOLAR ANALYSIS ===');
    console.log('Score:', state.bipolar.score);
    console.log('Current Phase:', state.bipolar.currentPhase);
    console.log('Elevated indicators:', state.bipolar.indicators.elevated);
    console.log('Depressed indicators:', state.bipolar.indicators.depressed);
    return state.bipolar;
  },

  resetSpectrum: () => {
    stateDetection.reset();
    console.log('✅ State detection reset');
  },

  // ==========================================
  // SYSTEM CHECKS
  // ==========================================
  
  fullCheck: async () => {
    console.log('=== BABA AI SYSTEM CHECK ===\n');
    
    // LocalStorage
    try {
      localStorage.setItem('_test_', '_test_');
      localStorage.removeItem('_test_');
      console.log('✅ LocalStorage: Working');
    } catch (e) {
      console.log('❌ LocalStorage: Failed');
    }
    
    // Google Drive
    const gdrive = googleDriveService.isAuthenticated();
    console.log(gdrive ? '✅ Google Drive: Connected' : '⚠️ Google Drive: Not connected');
    
    // Vectorize
    try {
      const health = await vectorService.healthCheck();
      console.log(health.healthy ? '✅ Vectorize: Healthy' : '❌ Vectorize: Unhealthy');
    } catch (e) {
      console.log('❌ Vectorize: Error -', e.message);
    }
    
    // Time Awareness
    const timeCtx = timeAwareness.getCurrentContext();
    console.log(`✅ Time Awareness: ${timeCtx.timeOfDay}, ${timeCtx.dayOfWeek}`);
    
    // MCS
    const mcsLoaded = mcsAutoFetch.isLoaded();
    console.log(mcsLoaded ? '✅ MCS: Loaded' : '⚠️ MCS: Not loaded');
    
    // User Adaptation
    const profile = userAdaptation.getProfileSummary();
    console.log(`✅ User Adaptation: ${profile.totalMessages} messages analyzed, ${profile.confidence} confidence`);
    
    // Full Spectrum Detection
    const spectrum = profile.spectrum;
    console.log(`✅ Spectrum Detection: Primary=${spectrum.primary}, Traits=[${spectrum.traits.join(', ')}]`);
    if (spectrum.scores) {
      console.log(`   ADHD: ${spectrum.scores.adhd || 0}%, Autism: ${spectrum.scores.autism || 0}%, Bipolar: ${spectrum.scores.bipolar || 0}%, NT: ${spectrum.scores.neurotypical || 0}%`);
    }
    
    // Safety Status
    const safety = userAdaptation.getSafetyStatus();
    if (safety.needsAttention) {
      console.log(`⚠️ Safety: Flags active - ${safety.flags.join(', ')}`);
    } else {
      console.log('✅ Safety: No concerns');
    }
    
    // Token Service
    try {
      const count = tokenService.countTokens('test');
      console.log(`✅ Token Service: Working (test count: ${count})`);
    } catch (e) {
      console.log('❌ Token Service: Error');
    }
    
    console.log('\n=== CHECK COMPLETE ===');
  },

  // ==========================================
  // UTILITIES
  // ==========================================
  
  clearAll: () => {
    if (window.confirm('This will clear ALL Baba AI data. Are you sure?')) {
      localStorage.clear();
      console.log('✅ All data cleared. Refresh the page.');
    }
  },
  
  exportChats: () => {
    const chats = localStorage.getItem('baba-ai-chats');
    if (chats) {
      console.log('Copy this JSON:');
      console.log(chats);
    } else {
      console.log('No chats to export');
    }
  },

  help: () => {
    console.log(`
╔══════════════════════════════════════════════════════════════╗
║                    BABA AI DEV TOOLS                         ║
╠══════════════════════════════════════════════════════════════╣
║  GOOGLE DRIVE                                                ║
║    babaTest.gdriveStatus()     - Check connection status     ║
║    babaTest.gdriveConnect()    - Start OAuth flow            ║
║    babaTest.gdriveDisconnect() - Disconnect account          ║
║                                                              ║
║  VECTORIZE / MEMORY                                          ║
║    babaTest.vectorHealth()     - Check Vectorize health      ║
║    babaTest.vectorStats()      - Get index statistics        ║
║    babaTest.vectorSearch(q)    - Search archived chats       ║
║                                                              ║
║  TOKENS                                                      ║
║    babaTest.tokenCount(text)   - Count tokens in text        ║
║    babaTest.tokenEstimate()    - Estimate current chat       ║
║                                                              ║
║  TIME AWARENESS                                              ║
║    babaTest.timeContext()      - Current time context        ║
║    babaTest.timePatterns()     - Usage patterns              ║
║    babaTest.sessionDuration()  - Current session length      ║
║    babaTest.timeState()        - Detected state (ADHD)       ║
║                                                              ║
║  MCS AUTO-FETCH                                              ║
║    babaTest.mcsStatus()        - MCS cache status            ║
║    babaTest.mcsRefresh()       - Force MCS refresh           ║
║    babaTest.mcsPreview()       - Preview MCS content         ║
║                                                              ║
║  USER ADAPTATION                                             ║
║    babaTest.userProfile()      - View full user profile      ║
║    babaTest.adaptationInstructions() - Current AI hints      ║
║    babaTest.clearProfile()     - Reset user profile          ║
║    babaTest.simulateMessage(t) - Test message analysis       ║
║                                                              ║
║  🧠 FULL SPECTRUM STATE DETECTION                            ║
║    babaTest.spectrumScores()   - All spectrum scores         ║
║    babaTest.fullState()        - Complete state data         ║
║    babaTest.safetyStatus()     - Safety flags & concerns     ║
║    babaTest.adhdScore()        - ADHD analysis               ║
║    babaTest.autismScore()      - Autism spectrum analysis    ║
║    babaTest.bipolarScore()     - Bipolar analysis            ║
║    babaTest.resetSpectrum()    - Reset state detection       ║
║                                                              ║
║  SYSTEM                                                      ║
║    babaTest.fullCheck()        - Run all system checks       ║
║    babaTest.clearAll()         - Clear all data              ║
║    babaTest.exportChats()      - Export chats as JSON        ║
║    babaTest.help()             - Show this help              ║
╚══════════════════════════════════════════════════════════════╝

SPECTRUM COVERAGE:
  • Neurotypical (casual → power → heavy user)
  • ADHD (hyperactive, inattentive, combined)
  • Autism spectrum (sensory, routine, detail focus)
  • Bipolar (elevated, depressed, mixed, stable)
  • Anxiety (cross-cutting)
  • Safety monitoring (psychosis indicators)

All detection is for ADAPTATION, not diagnosis.
All data stays LOCAL in browser storage.
    `);
  }
};

// Auto-show help on load
console.log('🔧 Baba AI Dev Tools loaded. Type babaTest.help() for commands.');
